import { dayjs } from "@thalamos/common";
import { MhaStatus } from "../types/patientState/PatientState.js";
/**
 * The intrinsic hierarchy level of each status from its label alone. A detention
 * status is `active` here even if its detention has since expired — callers that
 * care about expiry (see {@link resolveDisplayedStatuses}) demote an expired
 * detention to the `expired` level, matching the "Unknown (Expired)"
 * display.
 *
 * Exhaustive by construction: this `Record` is keyed by `MhaStatus`, so a new
 * enum member fails the build here until it is explicitly classified.
 */
const STATUS_LEVEL = {
    [MhaStatus.Section136]: "active",
    [MhaStatus.Section5_4]: "active",
    [MhaStatus.Section5_2]: "active",
    [MhaStatus.Section4]: "active",
    [MhaStatus.Section2]: "active",
    [MhaStatus.Section3]: "active",
    [MhaStatus.Section17A]: "active",
    [MhaStatus.NotDetained]: "notDetained",
    [MhaStatus.Unknown]: "incomplete",
    [MhaStatus.Incomplete]: "incomplete",
    [MhaStatus.Expired]: "expired",
};
export const mhaStatusLevel = (status) => STATUS_LEVEL[status];
const LEVEL_RANK = {
    active: 0,
    notDetained: 1,
    incomplete: 2,
    expired: 3,
};
/**
 * The display order WITHIN the active level:
 * Section 136, 5(4), 5(2), 4, 2, 3, CTO. Section 136 sits first so it is the
 * "primary" status on a same-level tie.
 */
const ACTIVE_STATUS_ORDER = [
    MhaStatus.Section136,
    MhaStatus.Section5_4,
    MhaStatus.Section5_2,
    MhaStatus.Section4,
    MhaStatus.Section2,
    MhaStatus.Section3,
    MhaStatus.Section17A,
];
/**
 * A single canonical total order over statuses: primary key is the hierarchy
 * level, secondary key is the within-active order. Lower rank = higher display
 * priority — i.e. the status a patient should be displayed as when more than
 * one could apply.
 */
export const mhaStatusRank = (status) => {
    const level = mhaStatusLevel(status);
    const base = LEVEL_RANK[level] * 100;
    if (level === "active") {
        const index = ACTIVE_STATUS_ORDER.indexOf(status);
        return base + (index === -1 ? ACTIVE_STATUS_ORDER.length : index);
    }
    return base;
};
/**
 * "Active" = the row is the latest for its bucket and its detention has not yet
 * expired. The single definition of "active", so every status decision agrees
 * on what it means.
 */
export const isActiveLatest = (asOf) => (s) => s.isLatest &&
    !!s.state.mhaStatus.expiryDateTime &&
    dayjs(s.state.mhaStatus.expiryDateTime).isAfter(asOf);
/**
 * The effective display level of a row once expiry is taken into account: an
 * inactive (expired/ended) detention is shown as "Unknown (Expired)", i.e. the
 * Expired level, rather than at its former active level.
 */
const effectiveLevel = (row, asOf) => {
    const intrinsic = mhaStatusLevel(row.state.mhaStatus.status);
    if (intrinsic === "active" && !isActiveLatest(asOf)(row)) {
        return "expired";
    }
    return intrinsic;
};
/**
 * Decide which status row(s) to display for a patient, per the display
 * hierarchy rules:
 *
 *   - Two active detentions coexisting → `dual` (ordered by {@link mhaStatusRank}
 *     so Section 136 is the primary side).
 *   - Exactly one active detention → `single` (the active one; "if one is
 *     inactive, show only the active").
 *   - No active detention → `single`, chosen by the hierarchy over the inactive
 *     rows (Not Detained > Incomplete > Expired), Section 136 winning
 *     same-level ties.
 *
 * Never emits more than two statuses. "Active" reuses {@link isActiveLatest}.
 */
export function resolveDisplayedStatuses(states, asOf) {
    const latest = states.filter((s) => s.isLatest);
    if (latest.length === 0) {
        return { kind: "single", state: undefined };
    }
    const byRank = (a, b) => mhaStatusRank(a.state.mhaStatus.status) -
        mhaStatusRank(b.state.mhaStatus.status);
    const activeDetentions = latest
        .filter((s) => mhaStatusLevel(s.state.mhaStatus.status) === "active")
        .filter(isActiveLatest(asOf))
        .sort(byRank);
    const primary = activeDetentions[0];
    const secondary = activeDetentions[1];
    if (primary !== undefined && secondary !== undefined) {
        return { kind: "dual", primary, secondary };
    }
    if (primary !== undefined) {
        return { kind: "single", state: primary };
    }
    // No active detention: rank the inactive rows by effective level, with
    // Section 136 winning a same-level tie.
    const best = [...latest].sort((a, b) => {
        const levelDiff = LEVEL_RANK[effectiveLevel(a, asOf)] - LEVEL_RANK[effectiveLevel(b, asOf)];
        if (levelDiff !== 0) {
            return levelDiff;
        }
        return byRank(a, b);
    })[0];
    return { kind: "single", state: best };
}
//# sourceMappingURL=mhaStatusHierarchy.js.map